/**
 * Blockout Dates Page
 * 
 * Interface for managing user unavailability dates in the Prelude application.
 * Features include:
 * - List view of all blockout dates
 * - Adding, editing, and deleting blockout dates
 * - Calendar view for visualizing unavailability
 * - Team view for admins to see all team members' unavailability
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Fab,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Event as EventIcon,
  EventBusy as EventBusyIcon,
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';

import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../context/AuthContext';
import { customFetch } from '../utils/customFetch';
import { API_ENDPOINTS } from '../config/api';

const BlockoutDates = () => {
  const [blockoutDates, setBlockoutDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date(),
    description: '',
    allDay: true,
    startTime: new Date(),
    endTime: new Date(new Date().setHours(new Date().getHours() + 1)),
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [teamBlockoutDates, setTeamBlockoutDates] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { user, canEdit } = useAuth();

  // Get the appropriate date-fns locale based on the current language
  const getLocale = () => {
    switch (i18n.language) {
      case 'pt':
        return ptBR;
      case 'es':
        return es;
      default:
        return enUS;
    }
  };

  // Fetch user's blockout dates
  const fetchBlockoutDates = async () => {
    setLoading(true);
    try {
      const response = await customFetch.get(API_ENDPOINTS.BLOCKOUT_DATES.BASE);
      setBlockoutDates(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blockout dates:', err);
      setError(t('blockoutDates.errorLoading'));
      toast.error(t('blockoutDates.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch team blockout dates (admin only)
  const fetchTeamBlockoutDates = async () => {
    if (!canEdit()) return;
    
    setTeamLoading(true);
    try {
      const response = await customFetch.get(API_ENDPOINTS.BLOCKOUT_DATES.CHURCH_BLOCKOUT_DATES);
      setTeamBlockoutDates(response.data.data);
    } catch (err) {
      console.error('Error fetching team blockout dates:', err);
      toast.error(t('blockoutDates.errorLoadingTeam'));
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockoutDates();
  }, []);

  // Handle tab change and load team data if needed
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && teamBlockoutDates.length === 0) {
      fetchTeamBlockoutDates();
    }
  };

  // Handle form input changes
  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Handle date change
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date,
    });
  };

  // Handle time changes
  const handleTimeChange = (field) => (time) => {
    setFormData({
      ...formData,
      [field]: time,
    });
  };

  // Handle checkbox change
  const handleCheckboxChange = (event) => {
    setFormData({
      ...formData,
      allDay: event.target.checked,
    });
  };

  // Open form to add new blockout date
  const handleAddBlockoutDate = () => {
    setSelectedDate(null);
    setFormData({
      date: new Date(),
      description: '',
      allDay: true,
      startTime: new Date(),
      endTime: new Date(new Date().setHours(new Date().getHours() + 1)),
    });
    setOpenForm(true);
  };

  // Open form to edit blockout date
  const handleEditBlockoutDate = (blockoutDate) => {
    setSelectedDate(blockoutDate);
    setFormData({
      date: new Date(blockoutDate.date),
      description: blockoutDate.description || '',
      allDay: blockoutDate.allDay,
      startTime: blockoutDate.startTime ? new Date(blockoutDate.startTime) : new Date(),
      endTime: blockoutDate.endTime ? new Date(blockoutDate.endTime) : new Date(new Date().setHours(new Date().getHours() + 1)),
    });
    setOpenForm(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const payload = {
        date: formData.date,
        description: formData.description,
        allDay: formData.allDay,
        startTime: formData.allDay ? null : formData.startTime,
        endTime: formData.allDay ? null : formData.endTime,
      };

      if (selectedDate) {
        // Update existing blockout date
        await customFetch.patch(
          API_ENDPOINTS.BLOCKOUT_DATES.UPDATE_BLOCKOUT_DATE(selectedDate._id),
          payload
        );
        toast.success(t('blockoutDates.unavailabilityUpdated'));
      } else {
        // Create new blockout date
        await customFetch.post(API_ENDPOINTS.BLOCKOUT_DATES.BASE, payload);
        toast.success(t('blockoutDates.unavailabilityAdded'));
      }

      setOpenForm(false);
      fetchBlockoutDates();
    } catch (err) {
      console.error('Error saving blockout date:', err);
      toast.error(err.response?.data?.message || t('common.errorOccurred'));
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      await customFetch.delete(
        API_ENDPOINTS.BLOCKOUT_DATES.DELETE_BLOCKOUT_DATE(selectedDate._id)
      );
      toast.success(t('blockoutDates.unavailabilityDeleted'));
      setConfirmDelete(false);
      fetchBlockoutDates();
    } catch (err) {
      console.error('Error deleting blockout date:', err);
      toast.error(err.response?.data?.message || t('common.errorOccurred'));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'PPP', { locale: getLocale() });
    } catch (err) {
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (timeString) => {
    try {
      const time = parseISO(timeString);
      return format(time, 'p', { locale: getLocale() });
    } catch (err) {
      return timeString;
    }
  };

  if (loading && blockoutDates.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title={t('blockoutDates.title')}
        subtitle={t('blockoutDates.subtitle')}
        icon={<EventBusyIcon />}
        onAddClick={handleAddBlockoutDate}
        showAddButton={true}
        backButton={true}
        onBackClick={() => navigate('/services')}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs for My Blockout Dates and Team Blockout Dates */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="blockout dates tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('blockoutDates.title')} id="tab-0" aria-controls="tabpanel-0" />
          {canEdit() && (
            <Tab label={t('blockoutDates.viewTeamBlockoutDates')} id="tab-1" aria-controls="tabpanel-1" />
          )}
        </Tabs>
      </Box>

      {/* My Blockout Dates Tab */}
      <Box
        role="tabpanel"
        hidden={tabValue !== 0}
        id="tabpanel-0"
        aria-labelledby="tab-0"
      >
        {tabValue === 0 && (
          <>
            {blockoutDates.length === 0 ? (
              <Box textAlign="center" my={4}>
                <Typography variant="h6" color="text.secondary">
                  {t('blockoutDates.noBlockoutDates')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddBlockoutDate}
                  sx={{ mt: 2 }}
                >
                  {t('blockoutDates.addBlockoutDate')}
                </Button>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table aria-label="blockout dates table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('blockoutDates.date')}</TableCell>
                      <TableCell>{t('blockoutDates.description')}</TableCell>
                      <TableCell>{t('blockoutDates.allDay')}</TableCell>
                      <TableCell>{t('blockoutDates.startTime')}</TableCell>
                      <TableCell>{t('blockoutDates.endTime')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blockoutDates.map((blockoutDate) => (
                      <TableRow key={blockoutDate._id}>
                        <TableCell>{formatDate(blockoutDate.date)}</TableCell>
                        <TableCell>{blockoutDate.description}</TableCell>
                        <TableCell>{blockoutDate.allDay ? t('common.yes') : t('common.no')}</TableCell>
                        <TableCell>
                          {blockoutDate.allDay ? '-' : formatTime(blockoutDate.startTime)}
                        </TableCell>
                        <TableCell>
                          {blockoutDate.allDay ? '-' : formatTime(blockoutDate.endTime)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('common.edit')}>
                            <IconButton
                              onClick={() => handleEditBlockoutDate(blockoutDate)}
                              size="small"
                              sx={{ color: '#a259ff' }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <IconButton
                              onClick={() => {
                                setSelectedDate(blockoutDate);
                                setConfirmDelete(true);
                              }}
                              size="small"
                              sx={{ color: '#ff5252' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      {/* Team Blockout Dates Tab */}
      <Box
        role="tabpanel"
        hidden={tabValue !== 1}
        id="tabpanel-1"
        aria-labelledby="tab-1"
      >
        {tabValue === 1 && (
          <>
            {teamLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : teamBlockoutDates.length === 0 ? (
              <Box textAlign="center" my={4}>
                <Typography variant="h6" color="text.secondary">
                  {t('blockoutDates.noBlockoutDates')}
                </Typography>
              </Box>
            ) : (() => {
              // Group by user and merge date ranges
              const grouped = teamBlockoutDates.reduce((acc, b) => {
                const userId = b.user?._id;
                if (!userId) return acc;
                if (!acc[userId]) {
                  acc[userId] = { user: b.user, dates: [] };
                }
                acc[userId].dates.push(b);
                return acc;
              }, {});
              const groupedArray = Object.values(grouped);

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                  {groupedArray.map((group) => {
                    const userName = `${group.user.firstName} ${group.user.lastName}`;
                    const sortedDates = group.dates.sort((a, b) => new Date(a.date) - new Date(b.date));
                    const firstDate = formatDate(sortedDates[0].date);
                    const lastDate = formatDate(sortedDates[sortedDates.length - 1].date);
                    const period = sortedDates.length === 1 ? firstDate : `${firstDate} — ${lastDate}`;
                    const reason = sortedDates[0].description || t('blockoutDates.noReasonProvided');

                    return (
                      <Paper
                        key={group.user._id}
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                            {userName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {t('blockoutDates.unavailablePeriod')}: {period}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25 }}>
                            {t('blockoutDates.reason')}: {reason}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', ml: 2, flexShrink: 0, gap: 0.5 }}>
                          {group.user.phone && (
                            <Tooltip title={t('blockoutDates.sendSms')}>
                              <IconButton
                                size="small"
                                href={`sms:${group.user.phone}`}
                                sx={{ color: theme.palette.success.main }}
                              >
                                <SmsIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {group.user.email && (
                            <Tooltip title={t('blockoutDates.sendEmail')}>
                              <IconButton
                                size="small"
                                href={`mailto:${group.user.email}`}
                                sx={{ color: theme.palette.info.main }}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              );
            })()}
          </>
        )}
      </Box>

      {/* Add/Edit Blockout Date Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedDate ? t('blockoutDates.editBlockoutDate') : t('blockoutDates.addBlockoutDate')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocale()}>
              <DatePicker
                label={t('blockoutDates.date')}
                value={formData.date}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />

            <TextField
              label={t('blockoutDates.description')}
              value={formData.description}
              onChange={handleInputChange('description')}
              fullWidth
              multiline
              rows={2}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allDay}
                  onChange={handleCheckboxChange}
                  name="allDay"
                  color="primary"
                />
              }
              label={t('blockoutDates.allDay')}
            />

            {!formData.allDay && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TimePicker
                  label={t('blockoutDates.startTime')}
                  value={formData.startTime}
                  onChange={handleTimeChange('startTime')}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <TimePicker
                  label={t('blockoutDates.endTime')}
                  value={formData.endTime}
                  onChange={handleTimeChange('endTime')}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Box>
            )}
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>{t('blockoutDates.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {t('blockoutDates.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('blockoutDates.deleteBlockoutDate')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('blockoutDates.confirmDelete')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} color="primary">
            {t('blockoutDates.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      {!isDesktop && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={handleAddBlockoutDate}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default BlockoutDates;